import { Box, Group, Text, UnstyledButton } from "@mantine/core";
import { Link } from "react-router-dom";
import type { TablerIcon } from "@tabler/icons-react";
import type { SectorCardVisual } from "@/pages/home/sectorCardVisual.util";
import {
  getExpertiseChipIcon,
  getExpertiseChipVisual,
  getFocusSectorHref,
  getFocusSectorIcon,
  getFocusSectorVisual,
} from "./focusSectorIcon.util";

type ChipSize = "sm" | "md" | "lg";

const SIZE_STYLES: Record<
  ChipSize,
  { padX: number; padY: number; gap: number; icon: number; font: number; radius: number }
> = {
  sm: { padX: 8, padY: 5, gap: 6, icon: 13, font: 11, radius: 10 },
  md: { padX: 10, padY: 6, gap: 7, icon: 15, font: 12, radius: 12 },
  lg: { padX: 12, padY: 7, gap: 8, icon: 16, font: 13, radius: 12 },
};

type ColoredIconChipProps = {
  label: string;
  Icon: TablerIcon;
  visual: SectorCardVisual;
  size?: ChipSize;
  href?: string | null;
  strong?: boolean;
  /** Stretch to fill parent (e.g. half-width focus sector cell). */
  fill?: boolean;
};

export function ColoredIconChip({
  label,
  Icon,
  visual,
  size = "md",
  href,
  strong = false,
  fill = false,
}: ColoredIconChipProps) {
  const s = SIZE_STYLES[size];
  const content = (
    <Group
      gap={s.gap}
      wrap="nowrap"
      align="flex-start"
      px={s.padX}
      py={s.padY}
      w={fill ? "100%" : "fit-content"}
      maw="100%"
      style={{
        display: fill ? "flex" : "inline-flex",
        width: fill ? "100%" : "fit-content",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: s.radius,
        background: visual.iconBg,
        border: `1px solid ${visual.border}`,
        color: visual.iconColor,
      }}
    >
      <Box
        style={{
          width: s.icon + 6,
          height: s.icon + 6,
          borderRadius: 8,
          background: visual.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: visual.iconColor,
          marginTop: 1,
        }}
      >
        <Icon size={s.icon} stroke={1.75} />
      </Box>
      <Text
        fz={s.font}
        fw={strong ? 700 : 600}
        c={visual.titleColor}
        style={{
          minWidth: 0,
          flex: fill ? 1 : "0 1 auto",
          lineHeight: 1.35,
          whiteSpace: "normal",
          overflowWrap: "break-word",
          wordBreak: "normal",
        }}
      >
        {label}
      </Text>
    </Group>
  );

  if (!href) return content;

  return (
    <UnstyledButton
      component={Link}
      to={href}
      aria-label={`Open ${label} sector`}
      style={{
        display: fill ? "flex" : "inline-flex",
        width: fill ? "100%" : undefined,
        maxWidth: "100%",
        borderRadius: s.radius,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {content}
    </UnstyledButton>
  );
}

type FocusSectorChipProps = {
  label: string;
  icon?: string;
  size?: ChipSize;
  interactive?: boolean;
  fill?: boolean;
};

export function FocusSectorChip({
  label,
  icon,
  size = "sm",
  interactive = false,
  fill = false,
}: FocusSectorChipProps) {
  const Icon = getFocusSectorIcon(icon, label);
  const visual = getFocusSectorVisual(icon, label);
  const href = interactive ? getFocusSectorHref(icon) : null;
  return (
    <ColoredIconChip label={label} Icon={Icon} visual={visual} size={size} href={href} fill={fill} strong />
  );
}

type ExpertiseChipProps = {
  industry?: string | null;
  tag: string;
  size?: ChipSize;
};

export function ExpertiseChip({ industry, tag, size = "md" }: ExpertiseChipProps) {
  const Icon = getExpertiseChipIcon(industry, tag);
  const visual = getExpertiseChipVisual(industry);
  return <ColoredIconChip label={tag} Icon={Icon} visual={visual} size={size} strong />;
}
